import React, { FC, useMemo } from "react"

import { BespokeComponentFragmentFragment, SelfServiceHeroQueryQuery } from "@graphqlTypes"
import Hero from "@components/Hero/Hero"

import "./SelfServiceHero.scss"
import { graphql, useStaticQuery } from "gatsby"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"
import { Wistia } from "@helpscout/hs-marketing-shared-components"
import SelfServiceTryItNowBeaconHint from "./components/SelfServiceTryItNowBeaconHint"

type SelfServiceHeroJsonPayload = {
  title: string
}

const SelfServiceHero: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const {
      aiAnswersFlowImageData,
      aiAnswersImageData,
      fabImageData,
      helpCenterImageData,
    } = useStaticQuery<SelfServiceHeroQueryQuery>(graphql`
      query SelfServiceHeroQuery {
        fabImageData: contentfulAsset(contentful_id:{eq:"3LVAaQg6gMxnZEr2FL39fF"}) {
          imgixImage {
            gatsbyImageData(width: 73)
          }
        }
        helpCenterImageData: contentfulAsset(contentful_id:{eq:"48M8eTeiQSfl2LA2EmsnDg"}) {
          imgixImage {
            gatsbyImageData(width: 447)
          }
        }
        aiAnswersImageData: contentfulAsset(contentful_id:{eq:"AAhLEj49h9VI2gQl2TUOt"}) {
          imgixImage {
            gatsbyImageData(width: 447)
          }
        }
        aiAnswersFlowImageData: contentfulAsset(contentful_id:{eq:"zRbO82zcFnXJpVfANUuio"}) {
          imgixImage {
            gatsbyImageData(width: 390)
          }
        }
      }
    `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as SelfServiceHeroJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: HomeHero (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    return (
      <>
        <Hero className={`SelfServiceHero uid--${contentful_id}`}>
          <div className="container padding-top--L">
            <h1 className="type--headline-medium-6XL">{json.title}</h1>
          </div>
          <div className="SelfServiceHero--VideoComp">
            {aiAnswersImageData?.imgixImage?.gatsbyImageData && (
              <ImageAboveFold
                alt="Product screenshot: AI Answers"
                aspectRatio={894 / 1293}
                className="screenshot screenshot--left"
                imageData={aiAnswersImageData.imgixImage.gatsbyImageData}
              />
            )}
            {fabImageData?.imgixImage?.gatsbyImageData && (
              <ImageAboveFold
                alt="Beacon FAB"
                aspectRatio={146 / 153}
                className="beacon-fab"
                imageData={fabImageData.imgixImage.gatsbyImageData}
              />
            )}
            {helpCenterImageData?.imgixImage?.gatsbyImageData && (
              <ImageAboveFold
                alt="Product screenshot: Help Center"
                aspectRatio={894 / 1294}
                className="screenshot screenshot--right"
                imageData={helpCenterImageData.imgixImage.gatsbyImageData}
              />
            )}
            {aiAnswersFlowImageData?.imgixImage?.gatsbyImageData && (
              <ImageAboveFold
                alt="Product screenshot: AI Answers Flow"
                aspectRatio={1080 / 1880}
                className="video-poster"
                imageData={aiAnswersFlowImageData.imgixImage.gatsbyImageData}
              />
            )}
            <Wistia
              autoPlay
              id="gpb5nhhblb"
            />
          </div>
        </Hero>
        <SelfServiceTryItNowBeaconHint />
      </>
    )
  }

export default SelfServiceHero
